/**
 * Style resolver for fetching and caching CSS from contracts.
 *
 * Features:
 * - Theme contract style fetching (via metadata)
 * - Contract's own styles() function
 * - {{style ...}} tag resolution
 * - Inline ```css block extraction
 * - Style caching with TTL
 * - CSS scoping to prevent conflicts
 *
 * Resolution order (cascade):
 * 1. Theme contract styles (base layer)
 * 2. Contract's own styles() function
 * 3. {{style ...}} includes in content
 * 4. Inline ```css blocks in content
 */
import { callRender } from "./client";
import { parseStyles, createStyleKey, extractInlineCss, removeCssBlocks, } from "../parsers/style";
import { sanitizeCss, scopeCss, combineCss, createScopeClassName, } from "./cssSanitizer";
const DEFAULT_CACHE_TTL = 60000; // 60 seconds for styles (longer than includes)
/**
 * Resolve all styles for a contract's render output.
 *
 * Resolution order (cascade):
 * 1. Theme contract styles (base layer)
 * 2. Contract's own styles() function
 * 3. {{style ...}} includes in content
 * 4. Inline ```css blocks in content
 */
export async function resolveStyles(client, content, options) {
    console.log("[soroban-render] resolveStyles called for contract:", options.contractId);
    const cache = options.cache ?? new Map();
    const cacheTtl = options.cacheTtl ?? DEFAULT_CACHE_TTL;
    const shouldScope = options.scopeStyles ?? true;
    const shouldRemoveCssBlocks = options.removeCssBlocksFromContent ?? true;
    const sources = {
        includes: [],
        inline: [],
    };
    const cssLayers = [];
    const scopeClassName = createScopeClassName(options.contractId);
    // 1. Fetch theme styles if theme contract specified
    if (options.themeContractId) {
        const themeCss = await fetchContractStyles(client, options.themeContractId, undefined, cache, cacheTtl);
        if (themeCss) {
            const sanitized = sanitizeCss(themeCss);
            const scoped = shouldScope
                ? scopeCss(sanitized, options.themeContractId)
                : sanitized;
            cssLayers.push(`/* Theme: ${options.themeContractId.slice(0, 8)}... */\n${scoped}`);
            sources.theme = scoped;
        }
    }
    // 2. Fetch contract's own styles
    const contractCss = await fetchContractStyles(client, options.contractId, undefined, cache, cacheTtl);
    if (contractCss) {
        const sanitized = sanitizeCss(contractCss);
        const scoped = shouldScope
            ? scopeCss(sanitized, options.contractId)
            : sanitized;
        cssLayers.push(`/* Contract: ${options.contractId.slice(0, 8)}... */\n${scoped}`);
        sources.contract = scoped;
    }
    // 3. Parse and resolve {{style ...}} tags
    const parsed = parseStyles(content);
    for (const tag of parsed.styleTags) {
        const resolvedContractId = tag.contract === "SELF" ? options.contractId : tag.contract;
        const styleCss = await fetchContractStyles(client, resolvedContractId, tag.func, cache, cacheTtl);
        if (styleCss) {
            const sanitized = sanitizeCss(styleCss);
            const scoped = shouldScope
                ? scopeCss(sanitized, resolvedContractId)
                : sanitized;
            const funcName = tag.func || "styles";
            cssLayers.push(`/* Include: ${resolvedContractId.slice(0, 8)}...:${funcName} */\n${scoped}`);
            sources.includes.push(scoped);
        }
    }
    // 4. Extract and sanitize inline CSS blocks
    const inlineCss = extractInlineCss(content);
    if (inlineCss) {
        const sanitized = sanitizeCss(inlineCss);
        const scoped = shouldScope
            ? scopeCss(sanitized, options.contractId)
            : sanitized;
        cssLayers.push(`/* Inline CSS */\n${scoped}`);
        sources.inline.push(scoped);
    }
    // Process content
    let processedContent = parsed.content; // Style tags already removed
    if (shouldRemoveCssBlocks) {
        processedContent = removeCssBlocks(processedContent);
    }
    return {
        css: combineCss(cssLayers),
        content: processedContent,
        scopeClassName,
        sources,
    };
}
/**
 * Fetch styles from a contract's styles() or styles_func() function.
 */
async function fetchContractStyles(client, contractId, func, cache, cacheTtl) {
    console.log("[soroban-render] fetchContractStyles called for:", contractId, "func:", func);
    const cacheKey = createStyleKey(contractId, func);
    // Check cache
    const cached = cache.get(cacheKey);
    const now = Date.now();
    if (cached && now - cached.timestamp < cacheTtl) {
        return cached.css;
    }
    try {
        // Call render_styles() or render_styles_{func}()
        // callRender prepends "render_" so we pass "styles" or "styles_{func}"
        const functionName = func ? `styles_${func}` : "styles";
        const css = await callRender(client, contractId, { functionName });
        console.log("[soroban-render] fetchContractStyles got CSS length:", css?.length ?? 0);
        // Cache the result
        cache.set(cacheKey, { css, timestamp: now });
        return css;
    }
    catch (err) {
        // Contract may not have a render_styles function - that's OK
        console.error("[soroban-render] fetchContractStyles error for", contractId, ":", err);
        return null;
    }
}
/**
 * Create a style resolver instance with shared cache.
 */
export function createStyleResolver(client, cacheTtl = DEFAULT_CACHE_TTL) {
    const cache = new Map();
    return {
        /**
         * Resolve all styles for content.
         */
        resolve: (content, options) => {
            return resolveStyles(client, content, { ...options, cache, cacheTtl });
        },
        /**
         * Clear the cache.
         */
        clearCache: () => {
            cache.clear();
        },
        /**
         * Get cache size.
         */
        getCacheSize: () => {
            return cache.size;
        },
    };
}
//# sourceMappingURL=styleResolver.js.map